import React, { useState } from 'react';
import { format } from 'date-fns';
import { getPlaybookNameLabel } from '../../constants/labels';

// Replicates the types from SessionDetail.tsx for the playbook usage data
interface SalesPlaybookQuestionAsked {
  questionId: string;
  questionText: string;
  tags: string[];
  askedAt: string;
}

interface SalesPlaybookQuestionNote {
  questionId: string;
  questionText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookPitchUsed {
  pitchId: string;
  pitchText: string;
  usedAt: string;
}

interface SalesPlaybookPitchNote {
  pitchId: string;
  pitchText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookAdvancedUsed {
  advancedId: string;
  advancedText: string;
  usedAt: string;
}

interface SalesPlaybookAdvancedNote {
  advancedId: string;
  advancedText: string;
  note: string;
  timestamp: string;
}

interface SalesPlaybookRoleUsed {
  playbookName: string;
  playbookDisplayName: string;
  role: string;
  roleDisplayName: string;
  questionsAsked: SalesPlaybookQuestionAsked[];
  questionNotes: SalesPlaybookQuestionNote[];
  pitchesUsed: SalesPlaybookPitchUsed[];
  pitchNotes: SalesPlaybookPitchNote[];
  advancedUsed: SalesPlaybookAdvancedUsed[];
  advancedNotes: SalesPlaybookAdvancedNote[];
}

export interface SalesPlaybookUsage {
  selectedPlaybookName?: string;
  selectedPlaybookDisplayName?: string;
  selectedRole?: string;
  selectedRoleDisplayName?: string;
  totalQuestionsAvailable?: number;
  rolesUsed?: SalesPlaybookRoleUsed[];
}

interface PlaybookUsagePanelProps {
  salesPlaybookUsage?: SalesPlaybookUsage;
}

// Question tags are categories, not statuses: one neutral chip for all.
const TAG_CHIP_CLASS = 'bg-surface-sunken text-ink-secondary';

const TAG_LABELS: Record<string, string> = {
  situation: 'Situation',
  pain: 'Pain',
  cost_math: 'Cost Math',
  authority: 'Authority',
  timeline: 'Timeline',
  close_trigger: 'Close Trigger',
};

const PlaybookUsagePanel: React.FC<PlaybookUsagePanelProps> = ({ salesPlaybookUsage }) => {
  const [selectedPlaybookIndex, setSelectedPlaybookIndex] = useState(0);
  const [isPitchSectionCollapsed, setIsPitchSectionCollapsed] = useState(true);
  const [isQuestionsSectionCollapsed, setIsQuestionsSectionCollapsed] = useState(true);
  const [isAdvancedSectionCollapsed, setIsAdvancedSectionCollapsed] = useState(true);

  const allRolesUsed = salesPlaybookUsage?.rolesUsed ?? [];
  const rolesUsed = allRolesUsed.filter(
    (r) =>
      (r.pitchesUsed?.length ?? 0) > 0 ||
      (r.questionsAsked?.length ?? 0) > 0 ||
      (r.advancedUsed?.length ?? 0) > 0
  );

  if (!salesPlaybookUsage || rolesUsed.length === 0) {
    const playbookWasSelected = !!salesPlaybookUsage?.selectedPlaybookName;
    return (
      <div className="bg-surface-subtle border border-hairline rounded-lg p-6 text-center text-ink-muted">
        <div className="text-lg font-medium mb-1">
          {playbookWasSelected ? 'No Items Explicitly Tracked' : 'No Sales Playbook Used'}
        </div>
        <div className="text-sm">
          {playbookWasSelected
            ? 'The rep opened the playbook but did not mark any items as used. See the AI Coverage Analysis below for what was detected in the transcripts.'
            : 'This session did not use the Sales Playbook feature.'}
        </div>
      </div>
    );
  }

  const uniquePlaybooks = new Set(rolesUsed.map((r) => r.playbookName));
  const totalPitches = rolesUsed.reduce((sum, r) => sum + (r.pitchesUsed?.length ?? 0), 0);
  const totalQuestions = rolesUsed.reduce((sum, r) => sum + (r.questionsAsked?.length ?? 0), 0);
  const totalAdvanced = rolesUsed.reduce((sum, r) => sum + (r.advancedUsed?.length ?? 0), 0);
  const totalNotes = rolesUsed.reduce(
    (sum, r) =>
      sum + (r.questionNotes?.length ?? 0) + (r.pitchNotes?.length ?? 0) + (r.advancedNotes?.length ?? 0),
    0
  );

  const selectedIndex = Math.min(selectedPlaybookIndex, rolesUsed.length - 1);
  const selectedRole = rolesUsed[selectedIndex];

  const questionNotesMap: Record<string, string> = {};
  selectedRole?.questionNotes?.forEach((n) => {
    questionNotesMap[n.questionId] = n.note;
  });
  const pitchNotesMap: Record<string, string> = {};
  selectedRole?.pitchNotes?.forEach((n) => {
    pitchNotesMap[n.pitchId] = n.note;
  });
  const advancedNotesMap: Record<string, string> = {};
  selectedRole?.advancedNotes?.forEach((n) => {
    advancedNotesMap[n.advancedId] = n.note;
  });

  return (
    <div className="space-y-3">
      {/* Overall Summary */}
      <div className="bg-white rounded-lg p-3 border border-hairline">
        <h3 className="text-base font-semibold text-ink mb-2">Actual Playbook Usage</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <div className="text-xs text-ink-muted">Playbooks</div>
            <div className="text-xl font-bold text-ink">{uniquePlaybooks.size}</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted">Roles Used</div>
            <div className="text-xl font-bold text-ink">{rolesUsed.length}</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted">Pitches Used</div>
            <div className="text-xl font-bold text-ink">{totalPitches}</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted">Questions Asked</div>
            <div className="text-xl font-bold text-ink">{totalQuestions}</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted">Deal Progression</div>
            <div className="text-xl font-bold text-ink">{totalAdvanced}</div>
          </div>
          <div>
            <div className="text-xs text-ink-muted">Total Notes</div>
            <div className="text-xl font-bold text-ink">{totalNotes}</div>
          </div>
        </div>
      </div>

      {/* Role selection tabs */}
      <div className="bg-white rounded-lg p-3 border border-hairline">
        <h3 className="text-xs font-semibold text-ink mb-2">{getPlaybookNameLabel()} / Role Combination</h3>
        <div className="flex flex-wrap gap-2">
          {rolesUsed.map((roleData, idx) => {
            const isSelected = idx === selectedIndex;
            const isLastUsed =
              roleData.playbookName === salesPlaybookUsage?.selectedPlaybookName &&
              roleData.role === salesPlaybookUsage?.selectedRole;
            const pitchesCount = roleData.pitchesUsed?.length ?? 0;
            const questionsCount = roleData.questionsAsked?.length ?? 0;
            const advancedCount = roleData.advancedUsed?.length ?? 0;

            return (
              <button
                key={`role-tab-${idx}`}
                onClick={() => {
                  setSelectedPlaybookIndex(idx);
                  setIsPitchSectionCollapsed(true);
                  setIsQuestionsSectionCollapsed(true);
                  setIsAdvancedSectionCollapsed(true);
                }}
                type="button"
                className={`px-3 py-2 rounded-lg border text-left transition-all ${
                  isSelected
                    ? 'bg-signature-tint border-signature ring-2 ring-signature'
                    : 'bg-surface-subtle border-hairline hover:bg-surface-sunken'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${isSelected ? 'text-signature-strong' : 'text-ink'}`}>
                    {roleData.playbookDisplayName || roleData.playbookName || '—'}
                  </span>
                  <span className="text-ink-muted">/</span>
                  <span className={`font-medium ${isSelected ? 'text-signature-strong' : 'text-ink'}`}>
                    {roleData.roleDisplayName || roleData.role}
                  </span>
                  {isLastUsed && (
                    <span className="px-1.5 py-0.5 bg-success-tint text-success text-xs rounded">Last Used</span>
                  )}
                </div>
                <div className="text-xs text-ink-muted mt-1">
                  {pitchesCount} pitch{pitchesCount !== 1 ? 'es' : ''}, {questionsCount} question
                  {questionsCount !== 1 ? 's' : ''}, {advancedCount} deal progression
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected role details */}
      {selectedRole && (
        <div className="bg-white rounded-lg p-4 border border-hairline">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-hairline">
            <h3 className="text-base font-semibold text-ink">
              {selectedRole.playbookDisplayName || selectedRole.playbookName || '—'} /{' '}
              {selectedRole.roleDisplayName || selectedRole.role}
            </h3>
          </div>

          {/* Intro Pitches */}
          {selectedRole.pitchesUsed && selectedRole.pitchesUsed.length > 0 && (
            <div className="mb-2 border border-hairline rounded-lg overflow-hidden">
              <button
                onClick={() => setIsPitchSectionCollapsed(!isPitchSectionCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 bg-surface-subtle hover:bg-surface-sunken transition-colors"
                type="button"
              >
                <h4 className="text-xs font-semibold text-ink">
                  Intro Pitches ({selectedRole.pitchesUsed.length})
                </h4>
                <span
                  className="text-ink-muted text-xs"
                  style={{
                    transform: isPitchSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.15s',
                  }}
                >
                  &#9660;
                </span>
              </button>
              {!isPitchSectionCollapsed && (
                <div className="p-2 space-y-1.5">
                  {selectedRole.pitchesUsed.map((pitch, idx) => {
                    const note = pitchNotesMap[pitch.pitchId];
                    return (
                      <div key={`pitch-${idx}`} className="bg-surface-subtle rounded p-2">
                        <div className="flex items-start gap-2">
                          <span className="text-ink-muted text-sm">&#10003;</span>
                          <div className="flex-1">
                            <div className="text-sm text-ink">{pitch.pitchText}</div>
                            {note && (
                              <div className="mt-1 text-xs text-ink-secondary bg-surface-sunken border border-hairline rounded p-1.5">
                                <span className="font-medium">Note:</span> {note}
                              </div>
                            )}
                            <div className="text-xs text-ink-muted">
                              Used at {format(new Date(pitch.usedAt), 'HH:mm:ss')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Questions Asked */}
          {selectedRole.questionsAsked && selectedRole.questionsAsked.length > 0 && (
            <div className="mb-2 border border-hairline rounded-lg overflow-hidden">
              <button
                onClick={() => setIsQuestionsSectionCollapsed(!isQuestionsSectionCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 bg-surface-subtle hover:bg-surface-sunken transition-colors"
                type="button"
              >
                <h4 className="text-xs font-semibold text-ink">
                  Questions Asked ({selectedRole.questionsAsked.length})
                </h4>
                <span
                  className="text-ink-muted text-xs"
                  style={{
                    transform: isQuestionsSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.15s',
                  }}
                >
                  &#9660;
                </span>
              </button>
              {!isQuestionsSectionCollapsed && (
                <div className="p-2 space-y-1.5">
                  {selectedRole.questionsAsked.map((q, idx) => {
                    const note = questionNotesMap[q.questionId];
                    return (
                      <div key={`question-${idx}`} className="bg-surface-subtle rounded p-2">
                        <div className="flex items-start gap-2">
                          <span className="text-ink-muted text-sm">&#10003;</span>
                          <div className="flex-1">
                            <div className="text-sm text-ink">{q.questionText}</div>
                            {q.tags && q.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {q.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className={`px-1.5 py-0.5 rounded text-xs ${TAG_CHIP_CLASS}`}
                                  >
                                    {TAG_LABELS[tag] ?? tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            {note && (
                              <div className="mt-1 text-xs text-ink-secondary bg-surface-sunken border border-hairline rounded p-1.5">
                                <span className="font-medium">Note:</span> {note}
                              </div>
                            )}
                            <div className="text-xs text-ink-muted">
                              Asked at {format(new Date(q.askedAt), 'HH:mm:ss')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Deal Progression */}
          {selectedRole.advancedUsed && selectedRole.advancedUsed.length > 0 && (
            <div className="mb-2 border border-hairline rounded-lg overflow-hidden">
              <button
                onClick={() => setIsAdvancedSectionCollapsed(!isAdvancedSectionCollapsed)}
                className="w-full flex items-center justify-between px-3 py-2 bg-surface-subtle hover:bg-surface-sunken transition-colors"
                type="button"
              >
                <h4 className="text-xs font-semibold text-ink">
                  Deal Progression ({selectedRole.advancedUsed.length})
                </h4>
                <span
                  className="text-ink-muted text-xs"
                  style={{
                    transform: isAdvancedSectionCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    transition: 'transform 0.15s',
                  }}
                >
                  &#9660;
                </span>
              </button>
              {!isAdvancedSectionCollapsed && (
                <div className="p-2 space-y-1.5">
                  {selectedRole.advancedUsed.map((advanced, idx) => {
                    const note = advancedNotesMap[advanced.advancedId];
                    return (
                      <div key={`advanced-${idx}`} className="bg-surface-subtle rounded p-2">
                        <div className="flex items-start gap-2">
                          <span className="text-ink-muted text-sm">&#10003;</span>
                          <div className="flex-1">
                            <div className="text-sm text-ink">{advanced.advancedText}</div>
                            {note && (
                              <div className="mt-1 text-xs text-ink-secondary bg-surface-sunken border border-hairline rounded p-1.5">
                                <span className="font-medium">Note:</span> {note}
                              </div>
                            )}
                            <div className="text-xs text-ink-muted">
                              Used at {format(new Date(advanced.usedAt), 'HH:mm:ss')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!selectedRole.pitchesUsed?.length &&
            !selectedRole.questionsAsked?.length &&
            !selectedRole.advancedUsed?.length && (
              <div className="text-center text-ink-muted py-3">
                No items were used for this playbook/role combination.
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default PlaybookUsagePanel;
